/**
 * PO composer · section 1 — Vendor.
 *
 * Faithful Tailwind translation of the prototype's section 1 markup:
 *   - `.vendor-card`    → violet avatar + name-row (name + GSTIN-verified pill)
 *                         + two `.meta` lines (GSTIN · state · address; contact)
 *                         + a "Change" button (decorative — the real picker is
 *                         the select below, matching the prototype which shows
 *                         both).
 *   - `.vendor-history` → 3-cell strip (Orders placed · On-time delivery ·
 *                         Last order). V1 has no PO-history aggregation on the
 *                         wire yet, so these render as honest dashes / "—"
 *                         rather than the prototype's demo figures.
 *   - `.field-grid cols-2` → the Vendor select (live, from `vendors.list`) +
 *                         a disabled INR currency select ("V1 supports INR only").
 *
 * The vendor card only renders once a vendor is selected; before that the
 * select stands alone (mirrors how the bill composer degrades).
 *
 * @see prototype/purchases/purchase-order-new.html §"1 · Vendor"
 */
import { Check, SquarePen } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@flint/ui";
import { PoField, PoFieldGrid, PoSectionCard } from "./po-section-card.js";

export interface PoVendorOption {
  id: string;
  name: string;
  gstin: string | null;
  state: string | null;
  city: string | null;
  contactName: string | null;
  email: string | null;
  phone: string | null;
}

interface PoVendorSectionProps {
  vendors: PoVendorOption[];
  vendorId: string;
  loading: boolean;
  /** Vendor is locked on edit (the update contract doesn't accept vendorId). */
  locked: boolean;
  error?: string | undefined;
  onVendorChange: (id: string) => void;
}

export function PoVendorSection({
  vendors,
  vendorId,
  loading,
  locked,
  error,
  onVendorChange,
}: PoVendorSectionProps) {
  const vendor = vendors.find((v) => v.id === vendorId) ?? null;
  const stateCode = vendor?.gstin?.slice(0, 2) ?? "";

  return (
    <PoSectionCard
      step="1"
      title="Vendor"
      description="Who you're ordering from. GSTIN drives GST mode (intra-state vs. inter-state)."
    >
      <div className="grid gap-3.5">
        {vendor ? (
          <>
            {/* .vendor-card */}
            <div className="flex items-start gap-3.5 rounded-sm border border-line bg-raised p-3.5">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-sm bg-violet-600 text-sm font-bold text-white">
                {vendorInitials(vendor.name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-ink">{vendor.name}</span>
                  {vendor.gstin ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-2 py-px text-xs font-semibold text-emerald-700">
                      <Check className="h-2.5 w-2.5" strokeWidth={2.4} />
                      GSTIN verified
                    </span>
                  ) : null}
                </div>
                <div className="text-xs leading-relaxed text-muted">
                  <strong className="font-medium text-ink-2">
                    {vendor.gstin ?? "GSTIN not on file"}
                  </strong>
                  {vendor.state ? ` · ${vendor.state}${stateCode ? ` (${stateCode})` : ""}` : ""}
                  {vendor.city ? ` · ${vendor.city}` : ""}
                </div>
                <div className="text-xs leading-relaxed text-muted">{formatContact(vendor)}</div>
              </div>
              {/* .change — decorative; the live picker is the select below */}
              <span className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-sm border border-line bg-paper px-3 text-xs font-semibold text-ink-2">
                <SquarePen className="h-[11px] w-[11px]" />
                Change
              </span>
            </div>

            {/* .vendor-history — honest placeholders; no PO-history wire yet */}
            <div className="grid grid-cols-3 gap-2 rounded-sm border border-line bg-paper px-3 py-2.5">
              <VendorHistoryCell label="Orders placed" value="—" />
              <VendorHistoryCell label="On-time delivery" value="—" />
              <VendorHistoryCell label="Last order" value="—" />
            </div>
          </>
        ) : null}

        {/* .field-grid cols-2 — Vendor select + disabled INR currency */}
        <PoFieldGrid cols={2}>
          <PoField
            id="po-vendor"
            label="Vendor"
            required
            error={error}
            help={locked ? "Vendor can't change after the order is created." : "Type to search all vendors"}
          >
            <Select value={vendorId || ""} onValueChange={onVendorChange} disabled={locked || loading}>
              <SelectTrigger
                id="po-vendor"
                aria-label="Vendor"
                className="h-9 min-w-0 overflow-hidden text-sm"
              >
                <SelectValue
                  placeholder={loading ? "Loading…" : "Select vendor"}
                  className="block min-w-0 truncate"
                />
              </SelectTrigger>
              <SelectContent>
                {vendors.map((v) => (
                  <SelectItem key={v.id} value={v.id} className="max-w-72">
                    <span className="block truncate">
                      {v.name}
                      {v.city ? ` · ${v.city}` : ""}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </PoField>
          <PoField id="po-currency" label="Currency" help="V1 supports INR only">
            <Select value="INR" disabled>
              <SelectTrigger id="po-currency" aria-label="Currency" className="h-9 text-sm" disabled>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="INR">INR · Indian Rupee</SelectItem>
              </SelectContent>
            </Select>
          </PoField>
        </PoFieldGrid>
      </div>
    </PoSectionCard>
  );
}

function VendorHistoryCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">
        {label}
      </div>
      <div className="mt-0.5 font-mono text-xs font-semibold text-ink">{value}</div>
    </div>
  );
}

function vendorInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? words[0]?.[1] ?? "")).toUpperCase();
}

function formatContact(vendor: PoVendorOption): string {
  const parts = [
    vendor.contactName ? `Contact ${vendor.contactName}` : null,
    vendor.email,
    vendor.phone,
  ].filter((p): p is string => Boolean(p));
  return parts.length > 0 ? parts.join(" · ") : "Contact details not on file";
}
